import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

export type PlatformAdmin = {
  id: number;
  name: string;
  email: string;
  is_active: boolean;
  last_login_at: string | null;
};

type PlatformAuthState = {
  token: string | null;
  admin: PlatformAdmin | null;

  setToken: (token: string | null) => void;
  setAdmin: (admin: PlatformAdmin | null) => void;
  clear: () => void;
};

/**
 * The operator's session. Deliberately NOT the tenant one.
 *
 * A separate store, a separate localStorage key and a separate axios instance,
 * mirroring the two separate principals on the server. One store with a
 * `scope` field would work right up until some shared code read the wrong
 * branch — and the failure mode there is a cashier's token reaching the
 * console, or an operator's reaching a till.
 *
 * Physically separating them means that bug cannot be written: there is no
 * expression anywhere that could evaluate to the wrong token.
 *
 * Both sessions can be live at once, on purpose. An operator investigating a
 * support ticket has the console open in one tab and the customer's workspace
 * — via impersonation — in another, and neither should sign the other out.
 */
export const usePlatformAuthStore = create<PlatformAuthState>()(
  persist(
    (set) => ({
      token: null,
      admin: null,

      setToken: (token) => set({ token }),
      setAdmin: (admin) => set({ admin }),
      clear: () => set({ token: null, admin: null }),
    }),
    {
      name: 'voltra.platform',
      storage: createJSONStorage(() => localStorage),
      // Only the token survives a reload. The admin is re-fetched from /me on
      // boot, so a deactivated account cannot keep a stale name and avatar on
      // screen after access has been revoked.
      partialize: (state) => ({ token: state.token }),
    },
  ),
);

export const usePlatformAdmin = () => usePlatformAuthStore((s) => s.admin);
export const usePlatformToken = () => usePlatformAuthStore((s) => s.token);
